import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Project, KickerEligiblePerson } from '../types/project';
import { Client, ClientLifecycleStage } from '../types/crm';
import { projectAPI } from '../utils/supabase/client';

// Map project status to client lifecycle stage
const statusToLifecycle: Record<string, ClientLifecycleStage> = {
  active: 'active',
  inactive: 'paused',
  canceled: 'churned',
  prospective: 'opportunity',
};

// Get current month in YYYY-MM format
const getCurrentMonth = () => new Date().toISOString().substring(0, 7);

// Convert a Client to Project format for backwards compatibility with ProjectManager
export function clientToProject(client: Client & { forecasts?: Array<{ forecast_month: string; ad_hoc_revenue?: number }> }): Project {
  // For variable revenue clients, retainer is always 0 — revenue comes from ad-hoc
  // entries. One-off deals are a single payment, so they carry no monthly retainer.
  let revenue: number;
  if (client.revenue_type === 'variable' || client.revenue_type === 'one_off') {
    revenue = 0;
  } else {
    revenue = client.confirmed_monthly_revenue || client.estimated_monthly_value || 0;
  }

  // Build ad-hoc forecasts for variable projects
  const adHocForecasts = client.revenue_type === 'variable' && client.forecasts
    ? client.forecasts
        .filter(f => f.ad_hoc_revenue && f.ad_hoc_revenue > 0)
        .map(f => ({ month: f.forecast_month, amount: f.ad_hoc_revenue! }))
    : undefined;

  return {
    id: client.id,
    projectName: client.company_name,
    status: client.lifecycle_stage === 'active' ? 'active' :
            client.lifecycle_stage === 'paused' ? 'inactive' :
            client.lifecycle_stage === 'churned' ? 'canceled' : 'prospective',
    clientId: client.id,
    revenueType: client.revenue_type || 'recurring',
    adHocForecasts,
    baseTemplate: {
      revenue,
      profitMargin: client.profit_margin || 50,
      operationsOverheadPercentage: client.operations_overhead_percentage || 4,
      operationsCompensationPerOwner: client.operations_compensation_per_owner || 2500,
      strategySponsor: client.strategy_sponsor?.name || 'Ken',
      operationsOwners: client.operationsOwners || [],
      tacticalSpecialists: (client.tacticalAssignments || []).map(ta => ({
        id: ta.id || crypto.randomUUID(),
        name: ta.role_name,
        amount: ta.monthly_amount,
        handlesKickers: ta.handles_kickers,
        assignedOwner: ta.assigned_to?.name || ta.owner?.name,
      })),
      performanceKickers: (client.performanceKickers || []).map(pk => ({
        id: pk.id || crypto.randomUUID(),
        name: pk.name,
        marketingSpend: pk.marketing_spend,
        selectedTier: pk.tier?.name || 'none',
        eligiblePeople: (pk.eligible_people || []) as KickerEligiblePerson[],
        sharePercentage: pk.specialist_share_percentage || 10,
      })),
    },
    monthlyOverrides: {},
    createdAt: new Date(client.created_at),
    updatedAt: new Date(client.updated_at),
  };
}

// Context type
export interface ProjectContextType {
  projects: Project[];
  loading: boolean;
  error: string | null;
  refreshProjects: () => Promise<void>;
  saveProject: (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>, projectId?: string, userEmail?: string) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  setError: (error: string | null) => void;
}

const ProjectContext = createContext<ProjectContextType | null>(null);

export function useProjects() {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within ProjectProvider');
  }
  return context;
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      // First check API health
      const health = await projectAPI.healthCheck();
      if (health.status === 'unreachable') {
        console.error('API health check failed:', health.error);
        throw new Error(`Unable to connect to server. ${health.error || 'Please check your connection.'}`);
      }

      // Load active clients with financial data in a single request
      const clients = await projectAPI.getClients({
        excludeChurned: true,
        withFinancials: true,
      });

      // Filter to only active/committed clients for Portfolio and convert to Project format
      const projectsFromClients = clients
        .filter(c => c.lifecycle_stage === 'active' || c.lifecycle_stage === 'committed')
        .map(client => clientToProject(client));

      setProjects(projectsFromClients);
    } catch (err) {
      console.error('Error loading projects:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      // Silently skip when there is no auth session — user is on the login page
      if (errorMessage.includes('No active session') || errorMessage.includes('Please sign in')) {
        return;
      }
      if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        setError('API endpoint not found. The server may need to be redeployed.');
      } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        setError('Unable to connect to server. Please check your internet connection.');
      } else if (errorMessage.includes('Unable to connect')) {
        setError(errorMessage);
      } else {
        setError(`Failed to load projects: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveProject = useCallback(async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>, projectId?: string, _userEmail?: string): Promise<void> => {
    setError(null);

    try {
      if (!projectId) {
        throw new Error('Project creation should be done through CRM client creation');
      }

      const existingProject = projects.find(p => p.id === projectId);
      if (!existingProject?.clientId) {
        throw new Error('Project has no linked client');
      }

      const clientId = existingProject.clientId;

      // Update financial configuration via CRM client endpoint
      await projectAPI.updateClientFinancial(clientId, {
        confirmed_monthly_revenue: projectData.revenueType === 'variable' ? 0 : (projectData.baseTemplate?.revenue ?? 0),
        profit_margin: projectData.baseTemplate?.profitMargin,
        operations_overhead_percentage: projectData.baseTemplate?.operationsOverheadPercentage,
        operations_compensation_per_owner: projectData.baseTemplate?.operationsCompensationPerOwner,
        strategySponsor: projectData.baseTemplate?.strategySponsor,
        operationsOwners: projectData.baseTemplate?.operationsOwners,
        tacticalAssignments: projectData.baseTemplate?.tacticalSpecialists?.map(ts => ({
          role_name: ts.name,
          monthly_amount: ts.amount,
          handles_kickers: ts.handlesKickers,
          assignedOwner: ts.assignedOwner,
        })),
        performanceKickers: projectData.baseTemplate?.performanceKickers?.map(pk => ({
          name: pk.name,
          marketing_spend: pk.marketingSpend,
          selectedTier: pk.selectedTier,
          specialist_share_percentage: pk.sharePercentage,
          eligible_people: pk.eligiblePeople,
        })),
      });

      // Handle status change via transition if needed
      const currentLifecycle = statusToLifecycle[existingProject.status] || 'active';
      const newLifecycle = statusToLifecycle[projectData.status] || 'active';

      if (currentLifecycle !== newLifecycle) {
        await projectAPI.transitionClient(clientId, { to_stage: newLifecycle });
      }

      // Refresh projects to get updated data
      await loadProjects();
      toast.success('Project saved');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to save project: ${msg}`);
      throw err;
    }
  }, [projects]);

  const deleteProject = useCallback(async (projectId: string) => {
    setError(null);
    try {
      const project = projects.find(p => p.id === projectId);
      if (project?.clientId) {
        await projectAPI.transitionClient(project.clientId, {
          to_stage: 'churned',
          churn_reason: 'project_completed',
        });
      }
      setProjects(prev => prev.filter(p => p.id !== projectId));
      toast.success('Project removed');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to delete project: ${msg}`);
      throw err;
    }
  }, [projects]);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const contextValue: ProjectContextType = {
    projects,
    loading,
    error,
    refreshProjects: loadProjects,
    saveProject,
    deleteProject,
    setError,
  };

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
}
